import React, { useContext, useEffect, useRef, useState } from 'react';
import { Textarea } from '@mantine/core';
import {
  Avatar,
  Checkbox,
  Modal,
  MultiSelect,
  Popover,
  Select,
  TextInput,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { DatePicker } from '@mantine/dates';
import CheckListItem from './checkListItem';
import { useDebouncedValue } from '@mantine/hooks';
import { AuthContext } from 'context/authContext';
import PrimaryButton from 'components/primaryButton';
import dayjs from 'dayjs';

import useGetAllTaskboardMembers from '../hooks/useGetAllTaskboardMembers';
import useGetAllBuckets from '../hooks/useGetAllBuckets';
import useDeleteSpecificTaskMember from '../hooks/useDeleteSpecificTaskMember';
import useAddMemberToSpecificTask from '../hooks/useAddMemberToSpecificTask';
import useGetSpecificTaskMembers from '../hooks/useGetSpecificTaskMembers';
import useUpdateTaskData from '../hooks/useUpdateTaskData';
import useAddTaskChecklist from '../hooks/useAddChecklist';
import useAddTaskComments from '../hooks/useAddTaskComment';
import useAddAttachment from '../hooks/useAddAttachments';
import useDeleteAttachement from '../hooks/useDeleteAttachment';

import { ReactComponent as CrossIcon } from 'assets/icons/crossIcon.svg';
import { ReactComponent as DownArrow } from 'assets/icons/downArrow.svg';
import { ReactComponent as CalenderIcon } from 'assets/icons/calenderIcon.svg';
import { ReactComponent as PdfIcon } from 'assets/images/pdfImage.svg';
import { ReactComponent as FileIcon } from 'assets/icons/fileIcon.svg';
import { ReactComponent as MenuDots } from 'assets/icons/menudots.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import { ReactComponent as EditIcon } from 'assets/icons/editIcon.svg';
import { ReactComponent as Tick } from 'assets/icons/rightIcon.svg';
import EditAttachmentModal from './editAttachmentModal';

export default function EditTaskModal({
  isModalOpen,
  setIsModalOpen,
  selectedTaskData,
}) {
  const { id } = useParams();
  const { authState } = useContext(AuthContext);
  const inputRef = useRef(null);

  const [checkListValue, setChecklistValue] = useState('');
  const [notesValue, setNotesValue] = useState('');
  const [commentsValue, setCommentsValue] = useState('');
  const [isAttachmentEditMenuOpened, setIsAttachmentEditMenuOpened] =
    useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState('');
  const [isEditAttachmentModalOpen, setEditAttachmentModalOpen] =
    useState(false);
  const [isEditTitle, setIsEditTitle] = useState(false);
  const [title, setTitle] = useState('');

  const [debounced] = useDebouncedValue(notesValue, 500);

  const closeModal = () => {
    setIsModalOpen(false);
    //setNotesValue('');
  };

  const getAllTaskBoardMembers = useGetAllTaskboardMembers(id);
  const getSpecificTaskMembers = useGetSpecificTaskMembers(
    selectedTaskData?.id
  );
  const getAllBuckets = useGetAllBuckets(id);
  const deleteSpecificTaskMember = useDeleteSpecificTaskMember();
  const addMemberToTaskMutation = useAddMemberToSpecificTask();
  const addTaskChecklistMutation = useAddTaskChecklist(
    setChecklistValue,
    selectedTaskData?.id
  );
  const updateTaskDataMutation = useUpdateTaskData();
  const addCommentsMutation = useAddTaskComments(
    setCommentsValue,
    selectedTaskData?.id
  );
  const addAttachmentsMutation = useAddAttachment(selectedTaskData?.id);
  const deleteAttachmentMutation = useDeleteAttachement(selectedTaskData?.id);

  //   custom dropdown item for select
  const SelectItem = ({
    value,
    label,
    image,
    name,
    description,
    ...others
  }) => {
    return (
      <div {...others} className='flex items-center py-2 cursor-pointer'>
        <Avatar src={image} color='red'>
          {label[0]?.toUpperCase()}
        </Avatar>
        <div className='pl-1'>
          <p className='text-18'>{label}</p>
          <p className='text-xs text-gray3 pt-0.5'>{description}</p>
        </div>
      </div>
    );
  };

  // update task members data
  const changeHandler = (data) => {
    if (
      getSpecificTaskMembers?.data?.data?.[0]?.members?.length > data?.length
    ) {
      const removedEmployee =
        getSpecificTaskMembers?.data?.data?.[0]?.members?.filter((member) => {
          return data.indexOf(`${member?.employee__id}`);
        });
      deleteSpecificTaskMember?.mutate({ id: removedEmployee[0]?.id });
    } else {
      if (
        data?.length > getSpecificTaskMembers?.data?.data?.[0]?.members?.length
      ) {
        addMemberToTaskMutation.mutate({
          task_id: selectedTaskData?.id,
          employee_id: data[data?.length - 1],
        });
      }
    }
  };

  // add comments
  const handleCommentsSubmit = (e) => {
    e.preventDefault();
    addCommentsMutation.mutate({
      task_id: selectedTaskData?.id,
      comment: commentsValue,
      by_user_id: authState?.user_id,
    });
  };

  useEffect(() => {
    setNotesValue(getSpecificTaskMembers?.data?.data[0]?.notes || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSpecificTaskMembers?.data?.data]);

  const handleAddChecklist = (e) => {
    e.preventDefault();
    addTaskChecklistMutation.mutate({
      task_id: ~~selectedTaskData?.id,
      checklist: checkListValue,
    });
  };

  // adding an attachment
  const handleFileChange = (e) => {
    const [file] = e.target.files;
    if (file) {
      const formData = new FormData();
      formData.append('task_id', selectedTaskData?.id);
      formData.append('attachment', file);
      addAttachmentsMutation.mutate(formData);
    }
  };

  useEffect(() => {
    if (debounced) {
      updateTaskDataMutation.mutate({
        updated_by_id: authState?.user_id,
        id: selectedTaskData?.id,
        notes: notesValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div>
      <Modal
        opened={isModalOpen}
        onClose={() => closeModal(false)}
        title={''}
        withCloseButton={false}
        overlayColor={'#F8F7F799'}
        overlayBlur={20}
        overlayOpacity={0.5}
        centered
        size='lg'
        radius='md'
        overflow='inside'
        transitionDuration={100}
        classNames={{
          title: 'w-full text-333333 font-semibold text-xl text-center',
          modal: 'pl-10',
          body: 'scroller -mr-4 pr-2',
        }}
      >
        <div>
          <div className='flex justify-between items-center'>
            <div className='flex space-x-3 items-center'>
              <Avatar
                color='red'
                size={40}
                radius='xl'
                classNames={{ root: 'border border-primary' }}
              >
                {selectedTaskData?.name?.[0]?.toUpperCase()}
              </Avatar>
              <div>
                <div className='flex items-center '>
                  {isEditTitle ? (
                    <TextInput
                      classNames={{
                        input:
                          ' text-[15px] bg-transparent border-[#FB7C51]  text-normal rounded pl-5',
                        label: 'text-[#8E8E8E] text-sm font-normal ',
                      }}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      autoFocus
                      // onBlur={() => setIsEditTitle(false)}
                    />
                  ) : (
                    <p className='text-lg text-333333 font-semibold'>
                      {getSpecificTaskMembers?.data?.data?.[0]?.name}
                    </p>
                  )}
                  {isEditTitle ? (
                    <Tick
                      className={`ml-3 ${
                        title ? 'cursor-pointer ' : 'hidden'
                      } `}
                      onClick={() => {
                        setIsEditTitle(false);
                        updateTaskDataMutation.mutate({
                          name: title,
                          updated_by_id: authState?.user_id,
                          id: selectedTaskData?.id,
                        });
                      }}
                    />
                  ) : (
                    <EditIcon
                      className='ml-3 cursor-pointer'
                      onClick={() => {
                        console.log('edit Title');
                        setIsEditTitle(true);
                        setTitle(getSpecificTaskMembers?.data?.data?.[0]?.name);
                      }}
                    />
                  )}
                </div>
                <p className='text-xs text-gray3 -mt-0.5'>
                  Last modified by
                  {' ' +
                    getSpecificTaskMembers?.data?.data?.[0]
                      ?.updated_by__first_name}
                </p>
              </div>
            </div>
            <CrossIcon
              className='w-12 cursor-pointer '
              onClick={() => {
                setIsModalOpen(false);
              }}
            />
          </div>
          <div className='mt-4'>
            <div className='w-full py-2 pr-4'>
              <MultiSelect
                value={getSpecificTaskMembers?.data?.data?.[0]?.members?.map(
                  (eachMember) => `${eachMember?.employee__id}`
                )}
                size='md'
                itemComponent={SelectItem}
                label='Assign members*'
                placeholder='Search or select'
                searchable
                onChange={(data) => changeHandler(data)}
                data={
                  getAllTaskBoardMembers?.data?.data?.length
                    ? getAllTaskBoardMembers?.data?.data?.map((item) => {
                        return {
                          value: `${item?.employee_id}`,
                          label: item?.employee__user__first_name,
                          image: item?.employee__profile_picture,
                          description: item?.employee__designation__name,
                        };
                      })
                    : []
                }
                rightSection={<DownArrow />}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                classNames={{
                  input: 'border-primary truncate min-h-[7vh]',
                  label: 'text-neutralsGrey text-sm',
                }}
              />
            </div>
            <div className='flex '>
              <div className='w-full py-2 pr-4'>
                <Select
                  value={`${getSpecificTaskMembers?.data?.data[0]?.bucket}`}
                  size='md'
                  label='Bucket*'
                  searchable
                  onChange={(value) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      bucket_id: value,
                    })
                  }
                  data={
                    getAllBuckets?.data?.data?.length
                      ? getAllBuckets?.data?.data?.map((item) => {
                          return {
                            value: `${item?.id}`,
                            label: item?.name,
                          };
                        })
                      : []
                  }
                  rightSection={<DownArrow />}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  classNames={{
                    input: 'border-primary truncate min-h-[7vh]',
                    label: 'text-neutralsGrey text-sm',
                  }}
                />
              </div>
              <div className='w-full py-2 pr-4'>
                <Select
                  value={getSpecificTaskMembers?.data?.data[0]?.progress}
                  size='md'
                  label='Progress*'
                  searchable
                  onChange={(value) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      progress: value,
                    })
                  }
                  data={[
                    { value: 'Not started', label: 'Not started' },
                    { value: 'In progress', label: 'In progress' },
                    { value: 'Completed', label: 'Completed' },
                  ]}
                  rightSection={<DownArrow />}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  classNames={{
                    input: 'border-primary truncate min-h-[7vh]',
                    label: 'text-neutralsGrey text-sm',
                  }}
                />
              </div>
              <div className='w-full py-2 pr-4'>
                <Select
                  size='md'
                  label='Priority*'
                  searchable
                  value={getSpecificTaskMembers?.data?.data[0]?.priority}
                  onChange={(value) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      priority: value,
                    })
                  }
                  data={[
                    { value: 'Urgent', label: 'Urgent' },
                    { value: 'Important', label: 'Important' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'Low', label: 'Low' },
                  ]}
                  rightSection={<DownArrow />}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  classNames={{
                    input: 'border-primary truncate min-h-[7vh]',
                    label: 'text-neutralsGrey text-sm',
                  }}
                />
              </div>
            </div>
            <div className='flex '>
              <div className='w-1/2 py-2 pr-4'>
                <DatePicker
                  maxDate={
                    getSpecificTaskMembers?.data?.data[0]?.end_date
                      ? dayjs(
                          new Date(
                            getSpecificTaskMembers?.data?.data[0]?.end_date
                          )
                        ).toDate()
                      : ''
                  }
                  value={
                    getSpecificTaskMembers?.data?.data[0]?.start_date
                      ? new Date(
                          getSpecificTaskMembers?.data?.data[0]?.start_date
                        )
                      : new Date()
                  }
                  onChange={(e) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      start_date: dayjs(e).format('YYYY-MM-DD'),
                    })
                  }
                  label='Start date*'
                  placeholder='Start anytime'
                  size='md'
                  rightSection={<CalenderIcon className='w-5 mr-2' />}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  classNames={{
                    input: 'border-primary h-12',
                    label: 'text-neutralsGrey text-sm',
                    yearPickerControlActive: 'text-white bg-primary',
                    monthPickerControlActive: 'text-white bg-primary',
                  }}
                />
              </div>
              <div className='w-1/2 py-2 px-4'>
                <DatePicker
                  minDate={
                    getSpecificTaskMembers?.data?.data[0]?.start_date
                      ? dayjs(
                          new Date(
                            getSpecificTaskMembers?.data?.data[0]?.start_date
                          )
                        ).toDate()
                      : new Date()
                  }
                  value={
                    getSpecificTaskMembers?.data?.data[0]?.end_date
                      ? new Date(
                          getSpecificTaskMembers?.data?.data[0]?.end_date
                        )
                      : null
                  }
                  onChange={(e) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      end_date: dayjs(e).format('YYYY-MM-DD'),
                    })
                  }
                  label='End date'
                  size='md'
                  rightSection={<CalenderIcon className='w-5 mr-2' />}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  classNames={{
                    input: 'border-primary h-12',
                    label: 'text-neutralsGrey text-sm',
                    yearPickerControlActive: 'text-white bg-primary',
                    monthPickerControlActive: 'text-white bg-primary',
                  }}
                />
              </div>
            </div>

            <Textarea
              value={notesValue}
              size='md'
              onChange={(e) => setNotesValue(e.currentTarget.value)}
              placeholder='Type a description or add notes here'
              label='Notes'
              classNames={{
                input: 'border-primary',
                label: 'text-neutralsGrey text-sm',
              }}
            />

            {/* checklist code start */}
            <div className='mt-5'>
              <div className='flex justify-between items-center mr-5 mb-2'>
                <p className='text-neutralsGrey text-sm '>
                  Checklist{' '}
                  {
                    getSpecificTaskMembers?.data?.data?.[0]
                      ?.completed_checklist_count
                  }{' '}
                  /{' '}
                  {
                    getSpecificTaskMembers?.data?.data?.[0]
                      ?.total_checklist_count
                  }
                </p>
                <Checkbox
                  label='Show on card'
                  checked={
                    getSpecificTaskMembers?.data?.data[0]
                      ?.show_checklist_on_card
                  }
                  onChange={(e) =>
                    updateTaskDataMutation.mutate({
                      updated_by_id: authState?.user_id,
                      id: selectedTaskData?.id,
                      show_checklist_on_card: e.currentTarget.checked,
                    })
                  }
                  classNames={{
                    label: 'text-neutralsGrey',
                  }}
                />
              </div>
              {getSpecificTaskMembers?.data?.data?.[0]?.checklist?.length ? (
                <>
                  {getSpecificTaskMembers?.data?.data?.[0]?.checklist?.map(
                    (item) => {
                      return (
                        <CheckListItem
                          item={item}
                          key={item?.id}
                          selectedTaskDataId={selectedTaskData?.id}
                        />
                      );
                    }
                  )}
                </>
              ) : null}
              <form onSubmit={(e) => handleAddChecklist(e)}>
                <div className='flex items-center w-full px-2'>
                  <Checkbox
                    classNames={{
                      input: 'rounded-full',
                    }}
                  />
                  <TextInput
                    value={checkListValue}
                    onChange={(e) => setChecklistValue(e.target.value)}
                    placeholder='Add an item & press "Enter"'
                    classNames={{ input: 'border-none w-96' }}
                  />
                </div>
              </form>
            </div>
            {/* checklist code end */}

            {/* attachments code start */}
            <div className='mt-5 mr-5 mb-2'>
              <p className='text-neutralsGrey text-sm mb-2'>Attachments</p>
              <div className='mb-4'>
                {getSpecificTaskMembers?.data?.data?.[0]?.attachments?.map(
                  (file) => {
                    return (
                      <div
                        className='border border-primary w-64 p-2 bg-FFE8E1 rounded-md mb-3 flex space-x-2 items-center select-none'
                        key={file?.id}
                      >
                        {file?.attachment?.split('.')?.at(-1) === 'png' ||
                        file?.attachment?.split('.')?.at(-1) === 'jpeg' ||
                        file?.attachment?.split('.')?.at(-1) === 'svg' ? (
                          <Avatar
                            src={file?.attachment}
                            className='rounded-full'
                          />
                        ) : file?.attachment?.split('.')?.at(-1) === 'pdf' ? (
                          <PdfIcon className='w-10 h-12' />
                        ) : (
                          <FileIcon className='w-10 h-8' />
                        )}
                        <a
                          href={file?.attachment}
                          target='_blank'
                          rel='noreferrer'
                        >
                          <div className='w-40'>
                            <p className='text-gray w-full truncate'>
                              {file?.file_name}
                            </p>
                            <p className='w-full truncate text-xs text-gray3'>
                              {file?.attachment}
                            </p>
                          </div>
                        </a>
                        <div className='relative'>
                          <MenuDots
                            className='w-4 h-5 cursor-pointer'
                            onClick={() => {
                              setSelectedAttachment(file);
                              setIsAttachmentEditMenuOpened(true);
                            }}
                          />
                          {selectedAttachment?.id === file?.id ? (
                            <div className='absolute top-0'>
                              <Popover
                                opened={isAttachmentEditMenuOpened}
                                onClose={() =>
                                  setIsAttachmentEditMenuOpened(false)
                                }
                                width={150}
                                position='bottom-start'
                                classNames={{
                                  dropdown: 'p-0',
                                }}
                              >
                                <div
                                  className='mb-2 select-none cursor-pointer  text-gray flex items-center space-x-2 hover:bg-FFE8E1 p-1 rounded-md -mx-2'
                                  onClick={() => {
                                    setIsAttachmentEditMenuOpened(false);
                                    setEditAttachmentModalOpen(true);
                                  }}
                                >
                                  <EditIcon className='w-5' />
                                  <p>Edit</p>
                                </div>
                                <div
                                  className='select-none cursor-pointer text-gray flex items-center space-x-2 hover:bg-FFE8E1 p-1 rounded-md -mx-2'
                                  onClick={() =>
                                    deleteAttachmentMutation.mutate({
                                      id: file?.id,
                                    })
                                  }
                                >
                                  <DeleteIcon className='w-5' />
                                  <p>Remove</p>
                                </div>
                              </Popover>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
              <button onClick={() => inputRef.current.click()} className='w-40'>
                <PrimaryButton isLoading={addAttachmentsMutation?.isLoading}>
                  Add attachments
                </PrimaryButton>
              </button>
              <input
                className='hidden'
                ref={inputRef}
                type='file'
                onChange={handleFileChange}
              />
            </div>
            {/* attachments code end */}

            {/* add and view comments code start */}
            <form onSubmit={(e) => handleCommentsSubmit(e)}>
              <div className='mt-2 mr-4'>
                <Textarea
                  value={commentsValue}
                  size='md'
                  onChange={(e) => setCommentsValue(e.currentTarget.value)}
                  placeholder='Type your message here'
                  label='Comments'
                  classNames={{
                    input: 'border-primary',
                    label: 'text-neutralsGrey text-sm',
                  }}
                />
                <div className='w-full flex justify-end'>
                  <button
                    className='w-16 text-xs mt-4 text-white bg-primary rounded-lg py-1.5 '
                    disabled={commentsValue?.length < 1}
                  >
                    Send
                  </button>
                </div>
              </div>
            </form>
            {getSpecificTaskMembers?.data?.data?.[0]?.comments?.length ? (
              <>
                {getSpecificTaskMembers?.data?.data?.[0]?.comments?.map(
                  (comment) => {
                    return (
                      <div key={comment?.id} className='mt-2 mb-4'>
                        <div className='flex items-center justify-between mr-5'>
                          <div className='flex items-center space-x-2 '>
                            <Avatar
                              radius='xl'
                              color='red'
                              size={32}
                              src={comment?.by_user__profile_picture}
                              classNames={{ root: 'border border-primary' }}
                            >
                              {comment?.by_user_id__first_name[0]?.toUpperCase()}
                            </Avatar>
                            <p className='text-gray3 text-lg'>
                              {comment?.by_user_id__first_name}
                            </p>
                          </div>
                          <p className='text-gray3 text-sm'>
                            {dayjs(comment?.updated_date).format(
                              'MMM DD, YYYY hh:mm A'
                            )}
                          </p>
                        </div>
                        <p className='text-gray pl-10'>{comment?.comment}</p>
                      </div>
                    );
                  }
                )}
              </>
            ) : null}
            {/* add and view comments code end */}
          </div>
        </div>
      </Modal>
      <EditAttachmentModal
        isModalOpen={isEditAttachmentModalOpen}
        setIsModalOpen={setEditAttachmentModalOpen}
        selectedAttachment={selectedAttachment}
        taskId={selectedTaskData?.id}
      />
    </div>
  );
}
